import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Badge, Spinner } from 'react-bootstrap';
import EventoService from '../../services/eventoService';
import { getTramites } from '../../services/tramiteService';
import PageHeader from '../../components/ui/PageHeader';
import '../../style/pages/HomePage.css';

/** Id del tipo RTI en BD (V2). */
const ID_TIPO_RTI_BD = 1;

/**
 * Página de inicio del área RTI: bienvenida, próximos eventos (de trámites RTI) y listado de trámites RTI.
 * Mismo formato que el área ART.
 */
const RTIInicio = () => {
    const navigate = useNavigate();
    const [proximosEventos, setProximosEventos] = useState([]);
    const [loadingEventos, setLoadingEventos] = useState(true);

    // Eventos próximos solo de trámites RTI, ordenados por proximidad
    useEffect(() => {
        const cargarEventos = async () => {
            setLoadingEventos(true);
            try {
                const [eventosRes, tramitesRTI] = await Promise.all([
                    EventoService.getEventos(),
                    getTramites(null, ID_TIPO_RTI_BD, null)
                ]);
                const eventos = eventosRes.data || [];
                const idsRTI = new Set((tramitesRTI || []).map((t) => t.idTramite).filter(Boolean));

                const hoy = new Date();
                hoy.setHours(0, 0, 0, 0);

                const eventosFuturos = eventos.filter((e) => {
                    const fechaStr = e.fecha?.includes('T') ? e.fecha : `${e.fecha || ''}T00:00:00`;
                    const fechaEvento = new Date(fechaStr);
                    const esDeRTI = !e.idTramite || idsRTI.has(e.idTramite);
                    return fechaEvento >= hoy && esDeRTI;
                });

                eventosFuturos.sort((a, b) => {
                    const fechaA = new Date(`${a.fecha}T${a.horaInicio || '00:00'}`);
                    const fechaB = new Date(`${b.fecha}T${b.horaInicio || '00:00'}`);
                    return fechaA - fechaB;
                });

                setProximosEventos(eventosFuturos.slice(0, 4));
            } catch (error) {
                console.error('Error al cargar eventos RTI:', error);
            } finally {
                setLoadingEventos(false);
            }
        };
        cargarEventos();
    }, []);

    const formatearFecha = (fechaStr) => {
        if (!fechaStr) return '';
        const part = (fechaStr.split('T')[0] || '').split('-');
        if (part.length < 3) return fechaStr;
        return `${part[2]}/${part[1]}/${part[0]}`;
    };

    const handleNavegacionEvento = (evento) => {
        if (evento.idTramite) {
            navigate(`/rti/tramites/${evento.idTramite}`);
        } else {
            navigate('/calendario');
        }
    };

    return (
        <div className="homepage-container area-rti">
            <PageHeader
                variante="plano"
                icono="folder-fill"
                titulo="Área RTI"
                descripcion="Reconocimiento de Tiempos de Incapacidad. Gestión de trámites y eventos RTI."
            />

            {/* Próximos eventos (solo de trámites RTI, ordenados por proximidad) */}
            <div className="eventos-section" style={{ marginBottom: '2rem' }}>
                <div className="eventos-header-row">
                    <h2 className="eventos-section-title">
                        <i className="bi bi-calendar-event me-2"></i>
                        Próximos eventos RTI
                    </h2>
                    <button
                        type="button"
                        className="btn-ver-calendario-home"
                        onClick={() => navigate('/calendario')}
                    >
                        Ver calendario
                        <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                </div>

                {loadingEventos ? (
                    <div className="loading-state">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mt-2 mb-0 small">Cargando eventos...</p>
                    </div>
                ) : proximosEventos.length === 0 ? (
                    <div className="empty-state-eventos compact">
                        <i className="bi bi-calendar-check"></i>
                        <p className="mb-0">No hay eventos próximos para trámites RTI.</p>
                    </div>
                ) : (
                    <Row xs={1} md={2} lg={4} className="g-3">
                        {proximosEventos.map((evento) => (
                            <Col key={evento.idEvento}>
                                <div
                                    className="evento-card-home"
                                    onClick={() => handleNavegacionEvento(evento)}
                                >
                                    <div className="evento-badges">
                                        <Badge bg="warning" text="dark" className="badge-tipo-home">
                                            {evento.tipoEvento || 'Evento'}
                                        </Badge>
                                        {evento.idTramite && (
                                            <Badge className="badge-tramite-home">Trámite #{evento.idTramite}</Badge>
                                        )}
                                    </div>
                                    <h6 className="evento-titulo-home" title={evento.titulo}>{evento.titulo}</h6>
                                    <div className="evento-info-home">
                                        <div className="info-item-home">
                                            <i className="bi bi-calendar3 me-2"></i>
                                            {formatearFecha(evento.fecha)}
                                        </div>
                                        <div className="info-item-home">
                                            <i className="bi bi-clock me-2"></i>
                                            {evento.todoElDia ? 'Todo el día' : `${evento.horaInicio?.substring(0, 5)} hs`}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>

            {/* Accesos rápidos RTI */}
            <div className="accesos-rapidos-home">
                <div className="acceso-card acceso-card-rti" onClick={() => navigate('/rti/tramites')}>
                    <i className="bi bi-folder2-open acceso-card-icon"></i>
                    <div className="acceso-card-body">
                        <h5>Trámites RTI</h5>
                        <p>Ver, editar e iniciar trámites de incapacidad</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                </div>
                <div className="acceso-card acceso-card-clientes" onClick={() => navigate('/clientes')}>
                    <i className="bi bi-people-fill acceso-card-icon"></i>
                    <div className="acceso-card-body">
                        <h5>Clientes</h5>
                        <p>Consultar y administrar el listado de clientes</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                </div>
                <div className="acceso-card acceso-card-agenda" onClick={() => navigate('/calendario')}>
                    <i className="bi bi-calendar-event-fill acceso-card-icon"></i>
                    <div className="acceso-card-body">
                        <h5>Agenda</h5>
                        <p>Ver todos los eventos y vencimientos programados</p>
                    </div>
                    <i className="bi bi-arrow-right-short acceso-card-arrow"></i>
                </div>
            </div>
        </div>
    );
};

export default RTIInicio;

